import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Trophy, Users, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

/** Shows the user's percentile on this exam among all ExamBro users + compare-with-topper. */
export default function PercentileCard({ attemptId }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/attempts/${attemptId}/percentile`).then((r) => setD(r.data)).catch(() => {});
  }, [attemptId]);

  if (!d || d.percentile === null || d.total_peers === 0) return null;
  if (d.total_peers < 2) return null; // no peer context yet

  const cmp = d.top_attempt;
  const you = { score: d.your_score_pct, accuracy: null };
  return (
    <Card className="p-6 rounded-md border-stone-200 shadow-none" data-testid="percentile-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-600 mb-1">
            App Percentile
          </div>
          <h3 className="font-heading text-xl font-semibold">
            {d.is_top ? "You're the top attempt" : `Top ${(100 - d.percentile).toFixed(1)}%`}
          </h3>
          <p className="text-xs text-stone-500 mt-1 font-mono">
            Ranked among {d.total_peers} graded attempts on this paper.
          </p>
        </div>
        <div className="text-right">
          <div className="font-heading text-4xl font-bold tabular-nums text-orange-600">
            {d.percentile}%
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500">
            percentile
          </div>
        </div>
      </div>

      {cmp && !d.is_top && (
        <div className="mt-4 pt-4 border-t border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-4 w-4 text-amber-500" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
              Compare with topper
              {d.top_author?.username && (
                <>
                  {" · "}
                  <Link
                    to={`/u/${d.top_author.username}`}
                    className="text-orange-600 hover:text-orange-700"
                    data-testid="topper-profile-link"
                  >
                    @{d.top_author.username}
                    <ArrowUpRight className="h-3 w-3 inline ml-0.5" />
                  </Link>
                </>
              )}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            <CompareCell label="Score" you={`${you.score}%`} top={`${cmp.score_pct}%`} />
            <CompareCell label="Accuracy" you="—" top={`${cmp.accuracy}%`} />
            <CompareCell label="Pressure" you="—" top={cmp.pressure_index} />
            <CompareCell label="Time" you="—" top={`${Math.round((cmp.time_used_seconds || 0) / 60)}m`} />
          </div>
        </div>
      )}

      {d.is_top && (
        <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-600" />
            <span className="text-sm text-amber-900 font-medium">
              You have the top score on this paper. Defend your title!
            </span>
          </div>
        </div>
      )}
    </Card>
  );
}

function CompareCell({ label, you, top }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-stone-500 mb-1">{label}</div>
      <div className="text-xs">
        <div className="font-mono text-stone-900">You: <span className="font-semibold">{you}</span></div>
        <div className="font-mono text-amber-700">Top: <span className="font-semibold">{top}</span></div>
      </div>
    </div>
  );
}
